/**
 * Offline Indicator Component
 * A small badge/indicator for the sidebar or header showing offline status
 */

import { WifiOff, Cloud, CloudOff, Loader2 } from 'lucide-react';
import { useOffline } from '@/context/OfflineContext';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const OfflineIndicator = ({ className, showLabel = false }) => {
  const { isOnline, pendingCount, isSyncing, lastSyncTime } = useOffline();

  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        icon: WifiOff,
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
        label: 'Offline',
        description: pendingCount > 0 
          ? `${pendingCount} change${pendingCount > 1 ? 's' : ''} pending`
          : 'Working in offline mode'
      };
    }
    
    if (isSyncing) {
      return {
        icon: Loader2,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
        label: 'Syncing...',
        description: `Syncing ${pendingCount} change${pendingCount > 1 ? 's' : ''}`,
        iconClass: 'animate-spin'
      };
    }
    
    if (pendingCount > 0) {
      return {
        icon: CloudOff,
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
        label: 'Pending sync',
        description: `${pendingCount} change${pendingCount > 1 ? 's' : ''} to sync`
      };
    }
    
    return {
      icon: Cloud,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      label: 'Online',
      description: lastSyncTime 
        ? `Last synced ${formatLastSync(lastSyncTime)}`
        : 'Connected'
    };
  };

  const status = getStatusConfig();
  const Icon = status.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-default",
              status.bgColor,
              className
            )}
            data-testid="offline-indicator"
          >
            <Icon className={cn("h-4 w-4", status.color, status.iconClass)} />
            {showLabel && (
              <span className={cn("text-xs font-medium", status.color)}>
                {status.label}
              </span>
            )}
            {pendingCount > 0 && !showLabel && (
              <span className={cn(
                "text-xs font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center",
                "bg-amber-500 text-white"
              )}>
                {pendingCount > 9 ? '9+' : pendingCount}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[200px]">
          <p className="font-medium">{status.label}</p>
          <p className="text-xs text-muted-foreground">{status.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default OfflineIndicator;
